import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useState } from 'react';
import { useSnackbar } from '@/alerts/index';
import { useAppConstantsContext } from '@/context/AppConstants';
import {
  ReportSectionStyled,
  ReportSectionTopBar,
} from '@/reports/shared/ReportSection/ReportSection';
import { TitleInfo } from '@/reports/shared/ReportSection/components';
import { getHowItWorksContent } from '@/reports/vehicle/utils';
import { Button, Box, Card, Stack, Text } from '@/theme/index';

export interface PremiumSectionVariationProps {
  sectionId: string;
  sectionName: string;
  reportUpgraded: boolean;
  nmvtisSuccess?: boolean;
  inSubsection?: boolean;
  handleFreeUpgrade: () => void;
  showTopBar?: boolean;
}

export const PremiumSectionVariation = ({
  sectionId,
  sectionName,
  reportUpgraded,
  nmvtisSuccess,
  inSubsection = false,
  handleFreeUpgrade,
  showTopBar,
}: PremiumSectionVariationProps) => {
  const {
    images: { imgPremiumData },
  } = useAppConstantsContext();
  const { enqueueSnackbar } = useSnackbar();
  const [upgradeReportCalled, setUpgradeReportCalled] = useState(false);
  const howItWorksContent = getHowItWorksContent(sectionName);
  const isInReportSectionHeaderVariation = useFeatureIsOn('bump-3961');

  const handleUpgrade = () => {
    handleFreeUpgrade();
    setUpgradeReportCalled(true);
  };

  if (reportUpgraded && !nmvtisSuccess && upgradeReportCalled) {
    enqueueSnackbar('Report upgrade failed. Please try again.', {
      variant: 'error',
    });
    setUpgradeReportCalled(false);
  }

  return (
    <ReportSectionStyled
      id={sectionId}
      flexDirection="column"
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        position: 'relative',
        boxShadow: inSubsection ? 'none' : '0 1px 2px 0 rgb(0 0 0 / 50%)',
        p: inSubsection ? 0 : 2.5,
      }}
    >
      {isInReportSectionHeaderVariation && showTopBar && (
        <ReportSectionTopBar />
      )}
      {!inSubsection && <TitleInfo title={sectionName} sectionId={sectionId} />}
      <Card variant="outlined">
        <Box display="flex" mb={2} flexDirection={{ xs: 'column', sm: 'row' }}>
          <Box m="auto" data-testid={`${sectionId}-image`}>
            <img src={imgPremiumData?.imgPremiumFrame} alt="Premium Data" />
          </Box>
          <Stack
            ml={{ xs: 0, sm: 4 }}
            mt={{ xs: 2, sm: 0 }}
            justifyContent="space-between"
            alignItems={{ xs: 'center', sm: 'flex-start' }}
            height={{ xs: '164px', sm: 'auto' }}
          >
            <Text variant="h2" data-testid={`${sectionId}-title`} m={0}>
              {sectionName}
            </Text>
            <Text
              variant="body1"
              data-testid={`${sectionId}-description`}
              textAlign={{ xs: 'center', sm: 'start' }}
            >
              {sectionName} may be available from our partner sources. Get
              access to a range of bonus data in your vehicle report.
            </Text>
            <Button
              variant="contained"
              color="success"
              onClick={handleUpgrade}
              data-testid={`${sectionId}-upgrade-button`}
              sx={{ fontSize: '0.875rem', height: '36px', width: '176px' }}
            >
              View {sectionName}
            </Button>
          </Stack>
        </Box>
        {howItWorksContent}
      </Card>
    </ReportSectionStyled>
  );
};
